import { useEffect, useMemo, useRef } from 'react';
import type { StripeElements } from '@stripe/stripe-js';
import { EXPRESS_ELEMENTS_BASE_OPTIONS } from '@/constants/stripe';
import { logError } from '@/utils/logger';

/**
 * Stable `<Elements options>` for express wallets.
 *
 * Inline `options={{ amount: priceTotal }}` remounts Stripe Elements on every
 * CheckoutContext write (email keystrokes, ZIP tax updates). Remount destroys
 * ExpressCheckoutElement and wallets disappear. Pin the mount-time amount and
 * sync later via {@link useSyncStripeElementsAmount}.
 */
export const useStableStripeExpressElementsOptions = (priceTotal: number, currencyCode: string) => {
  const currency = currencyCode.toLowerCase();
  const initialAmountRef = useRef(Math.round(priceTotal));

  return useMemo(
    () => ({
      mode: 'payment' as const,
      amount: initialAmountRef.current,
      currency,
      loader: 'never' as const,
      paymentMethodCreation: 'manual' as const,
      ...EXPRESS_ELEMENTS_BASE_OPTIONS,
    }),
    [currency]
  );
};

/** Keep Elements' payment amount current without remounting the provider. */
export const useSyncStripeElementsAmount = (elements: StripeElements | null, priceTotal: number): void => {
  useEffect(() => {
    if (!elements) return;
    const amount = Math.round(priceTotal);
    try {
      elements.update({ amount });
    } catch (error) {
      // Do not leave Express Checkout on the mount-time amount after tax/ZIP
      // updates — log and retry once after the current frame (Elements may not
      // accept update until fully ready).
      logError('Failed to sync Stripe Elements amount', { error, amount });
      const retry = window.setTimeout(() => {
        try {
          elements.update({ amount });
        } catch (retryError) {
          logError('Retry failed to sync Stripe Elements amount', { error: retryError, amount });
        }
      }, 0);
      return () => window.clearTimeout(retry);
    }
  }, [elements, priceTotal]);
};
